import React, { useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const Programs: React.FC = () => {
  const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
  
  const programs = [
    {
      title: "Language Programs",
      description: "Intensive Japanese language courses for international students",
      icon: "🈳",
      color: "from-secondary to-primary",
      benefits: [
        "Small class sizes for personalized attention",
        "Cultural activities included in curriculum",
        "JLPT examination preparation",
        "Pathway to degree programs"
      ],
      duration: "15, 18, 21, 24 months",
      admissionDeadline: "January, April, July, October",
      entryRequirements: "Basic Japanese language knowledge required"
    },
    {
      title: "Bachelor's Programs",
      description: "Undergraduate programs in various fields including Engineering, Business, and Arts",
      icon: "🎓",
      color: "from-primary to-primary/70",
      benefits: [
        // "Full academic credit transfer from your home country",
        // "English-taught programs available",
        "Cultural immersion opportunities",
        "Career development support"
      ],
      duration: "4 years",
      admissionDeadline: "April",
      entryRequirements: "High school diploma or equivalent"
    },
    {
      title: "Master's Programs",
      description: "Advanced studies and research opportunities in specialized fields",
      icon: "📚",
      color: "from-secondary to-secondary/70",
      benefits: [
        "Work with leading researchers in your field",
        "Access to cutting-edge research facilities",
        "International research collaboration opportunities",
        "Teaching assistant positions available"
      ],
      duration: "2 years",
      admissionDeadline: "April",
      entryRequirements: "Bachelor's degree in a related field"
    }
    // {
    //   title: "PhD Programs",
    //   description: "Doctoral research programs with world-class faculty guidance",
    //   icon: "🔬",
    //   color: "from-primary to-secondary",
    //   benefits: [
    //     "Full research funding opportunities",
    //     "Conference attendance stipends",
    //     "Publication support in international journals",
    //     "Industry partnership opportunities"
    //   ],
    //   duration: "3-5 years",
    //   admissionDeadline: "November 15th & June 30th",
    //   entryRequirements: "Master's degree with research experience"
    // }
    
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="programs" className="py-24 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 to-slate-100 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -left-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>
      
      {/* Japanese pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 select-none pointer-events-none">
        <div className="japanese-pattern"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-1 bg-secondary/10 text-secondary rounded-full text-sm font-medium mb-4"
          >
            Academic Excellence
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Our Programs
          </h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-slate-600 max-w-3xl mx-auto"
          >
            Discover a wide range of academic programs designed to help you achieve your educational goals in Japan.
          </motion.p>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {programs.map((program, index) => (
            <motion.div
              key={program.title}
              variants={itemVariants}
              className="group relative"
              onMouseEnter={() => setSelectedProgram(index)}
              onMouseLeave={() => setSelectedProgram(null)}
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 h-full transform-gpu hover:-translate-y-2 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br via-white to-white opacity-90"></div>
                
                <motion.div 
                  className={`w-16 h-16 rounded-xl bg-gradient-to-r ${program.color} flex items-center justify-center text-3xl mb-6 relative z-10`}
                  whileHover={{ 
                    scale: 1.1,
                    rotate: [0, 5], 
                    transition: { 
                      duration: 0.5,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }
                  }}
                >
                  {program.icon}
                </motion.div>
                
                <h3 className="text-xl font-semibold mb-3 text-slate-900 relative z-10">
                  {program.title}
                </h3>
                
                <p className="text-slate-600 relative z-10 mb-4">
                  {program.description}
                </p>
                
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10"
                  style={{ 
                    background: `radial-gradient(circle at top left, ${program.color.includes('primary') ? '#e30613' : '#0052A4'}, transparent 70%)`
                  }}
                />
                
                <motion.div
                  className="mt-6 relative z-10"
                  initial={{ opacity: 0, height: 0 }}
                  animate={selectedProgram === index ? { 
                    opacity: 1,
                    height: 'auto',
                    transition: { duration: 0.3 }
                  } : { 
                    opacity: 0,
                    height: 0,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Duration:</span>
                      <span className="font-medium">{program.duration}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Deadline:</span>
                      <span className="font-medium">{program.admissionDeadline}</span>
                    </div>
                  </div>
                </motion.div>
                
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className={`mt-6 w-full py-2 px-4 rounded-lg text-white text-sm font-medium bg-gradient-to-r ${program.color} relative z-10`}
                >
                  Learn More
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20 bg-white rounded-2xl shadow-xl p-8 lg:p-12 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br via-white to-white opacity-90"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary"></div>
          
          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-slate-900">Program Admission Process</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-slate-50 p-6 rounded-xl"
              >
                <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xl font-bold mb-4">1</div>
                <h4 className="text-lg font-semibold mb-2">Application</h4>
                <p className="text-slate-600 text-sm">Complete online application form and submit required documents</p>
              </motion.div>
              
              <motion.div
                whileHover={{ y: -5 }}
                className="bg-slate-50 p-6 rounded-xl"
              >
                <div className="w-12 h-12 bg-secondary/10 text-secondary rounded-full flex items-center justify-center text-xl font-bold mb-4">2</div>
                <h4 className="text-lg font-semibold mb-2">Selection</h4>
                <p className="text-slate-600 text-sm">Document screening and online interview with faculty members</p>
              </motion.div>
              
              <motion.div
                whileHover={{ y: -5 }}
                className="bg-slate-50 p-6 rounded-xl"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 text-slate-900 rounded-full flex items-center justify-center text-xl font-bold mb-4">3</div>
                <h4 className="text-lg font-semibold mb-2">Admission</h4>
                <p className="text-slate-600 text-sm">Receive admission letter and prepare for your journey to Japan</p>
              </motion.div>
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-6">Need help with your application or have questions about our programs?</p>
              <RouterLink to="/contact">
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all duration-300"
              >
                Contact an Advisor
              </motion.button>
              </RouterLink>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Programs; 